import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { CalendarDays, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { adminApproveRejectLeave, fetchAllLeaveRequests } from "@/services/leaveService";
import { useTeam } from "@/contexts/TeamContext";
import { announceNotificationsChanged } from "@/lib/notifications";
import { getErrorMessage } from "@/utils/errorHandler";

const formatDay = (value) => (value ? format(new Date(value), "d MMM yyyy") : "—");

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  approved: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  rejected: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  cancelled: "bg-muted text-muted-foreground",
};

// Where a pending request is, from the manager's point of view.
const stageLabel = (request) => {
  if (request.status !== "pending") return null;
  if (request.can_act) return "Waiting for you";
  return request.approval_stage === "hr" ? "With admin" : "Waiting";
};

/**
 * "My team" → Leave: the manager step of two-step leave approval. A manager
 * approves or rejects their direct reports' requests first; an admin then gives the
 * final decision.
 */
export default function TeamLeave() {
  const { duties } = useTeam();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("waiting");
  const [deciding, setDeciding] = useState(null); // { request, status }
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const data = await fetchAllLeaveRequests();
      setRequests(Array.isArray(data?.leave_requests) ? data.leave_requests : []);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to load your team's leave"));
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const waiting = useMemo(() => requests.filter((r) => r.can_act), [requests]);
  const shown = tab === "waiting" ? waiting : requests;

  const openDecision = (request, status) => {
    setDeciding({ request, status });
    setComment("");
  };

  const submitDecision = async () => {
    if (!deciding) return;
    try {
      setSaving(true);
      await adminApproveRejectLeave(deciding.request.id, deciding.status, comment.trim());
      toast.success(
        deciding.status === "approved"
          ? "Approved. It now goes to an admin for the final decision."
          : "Leave request rejected.",
      );
      setDeciding(null);
      announceNotificationsChanged();
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to save your decision"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground">Team leave</h1>
        <p className="text-muted-foreground mt-1">
          Review leave requests from the people who report to you. Your approval
          sends a request to an admin for the final decision.
        </p>
      </div>

      {!duties.leave ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Your company sends leave requests straight to admins, so there's nothing
            for you to approve here.
          </CardContent>
        </Card>
      ) : (
        <>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="flex justify-start space-x-8 overflow-x-auto bg-transparent h-auto p-0 no-scrollbar">
              <TabsTrigger className="w-fit px-4 text-left sm:w-auto" value="waiting">
                Waiting for you{waiting.length ? ` (${waiting.length})` : ""}
              </TabsTrigger>
              <TabsTrigger className="w-fit px-4 text-left sm:w-auto" value="all">
                All requests
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {loading ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-24 w-full rounded-xl" />
              ))}
            </div>
          ) : shown.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <CalendarDays className="mx-auto mb-3 h-8 w-8 opacity-60" />
                {tab === "waiting"
                  ? "Nothing waiting for you. You're all caught up."
                  : "No leave requests from your team yet."}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {shown.map((request) => {
                const stage = stageLabel(request);
                return (
                  <Card key={request.id}>
                    <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-foreground">
                            {request.employee_name}
                          </span>
                          <span className="text-xs text-muted-foreground">{request.emp_id}</span>
                          <Badge className={STATUS_STYLES[request.status] || STATUS_STYLES.cancelled}>
                            {request.status}
                          </Badge>
                          {stage && (
                            <Badge variant="outline" className="text-xs">
                              {stage}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-foreground">
                          {request.leave_type} · {formatDay(request.start_date)}
                          {request.end_date && request.end_date !== request.start_date
                            ? ` – ${formatDay(request.end_date)}`
                            : ""}{" "}
                          · {request.days} day{Number(request.days) === 1 ? "" : "s"}
                          {request.half_day ? " (half day)" : ""}
                        </p>
                        {request.reason && (
                          <p className="text-sm text-muted-foreground break-words">
                            {request.reason}
                          </p>
                        )}
                      </div>

                      {request.can_act && (
                        <div className="flex shrink-0 gap-2">
                          <Button
                            variant="outline"
                            className="text-destructive hover:text-destructive"
                            onClick={() => openDecision(request, "rejected")}
                          >
                            <X className="mr-1 h-4 w-4" /> Reject
                          </Button>
                          <Button onClick={() => openDecision(request, "approved")}>
                            <Check className="mr-1 h-4 w-4" /> Approve
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      <Dialog open={Boolean(deciding)} onOpenChange={(open) => !open && setDeciding(null)}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>
              {deciding?.status === "approved" ? "Approve leave" : "Reject leave"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {deciding?.request.employee_name} · {deciding?.request.leave_type} ·{" "}
            {formatDay(deciding?.request.start_date)}
            {deciding?.status === "approved"
              ? ". An admin gives the final decision after you."
              : ". They'll be told it was rejected."}
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="team-leave-comment">Comment (optional)</Label>
            <Textarea
              id="team-leave-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a note for the admin and the employee"
            />
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setDeciding(null)} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant={deciding?.status === "approved" ? "default" : "destructive"}
              onClick={submitDecision}
              disabled={saving}
            >
              {saving ? "Saving..." : deciding?.status === "approved" ? "Approve" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
